import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { colorsMapping } from '@repo/toolkit/colors'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Checkbox from '@repo/ui/checkbox'
import FilesSelection from '@repo/ui/files-selection'
import { UserCheckIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { getCountries } from '../../../../api/countries/get-countries'
import { getJourneyPlaces } from '../../../../api/journey-place/get-journey-places'
import { activateOl } from '../../../../api/ols/activate-ol'
import { deactivateOl } from '../../../../api/ols/deactivate-ol'
import { getOls } from '../../../../api/ols/get-ols'
import { getContactInParis } from '../../../../api/ols/gte-contact-in-paris'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  CurrencyEnum,
  currencyEnumMapping
} from '../../../../lib/types/enums/common/currency.enum'
import {
  genderEnumValues,
  genderOptionsMapping,
  toGenderEnum
} from '../../../../lib/types/enums/common/gender.enum'
import { OLTypeEnum, olTypeEnumMapping } from '../../../../lib/types/enums/ol/ol-type.enum'
import { OlInput } from '../../../../lib/types/models/ol/ol-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import {
  getSelectedEnumOption,
  getSelectedOption,
  showAxiosError
} from '../../../../lib/utils/functions/misc.functions'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { getOlName } from '../../../../views/ols/center/utils/functions'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import { ControlledAdressInput } from '../../../elements/inputs/address-input'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import { ProfileImageInput } from '../../../elements/inputs/profile-image-input'
import { StatusToggle } from '../../../elements/status-toggle'
import ValidationButtons from '../../../elements/validation-buttons'
import { OL_FORM_SCHEMA_VALIDATION } from '../utils'
import OlBankAccountsOlForm from './ol-bank-accounts-ol-form'
import OlCommentsOlForm from './ol-comments-ol-form'
import OlContactsOlForm from './ol-contacts-ol-form'
import OlJourneyPlaceOlForm from './ol-journey-place-ol-form'

type Props = BaseFormProps<OlInput, true> & {
  olId?: number
}

const CENTER_OL_TYPES: OLTypeEnum[] = [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE]

const OlForm = ({ olId, defaultValues, ...validationButtonProps }: Props) => {
  const { t, currentLanguage } = useTranslate()
  const form = useForm<OlInput>({
    resolver: zodResolver(OL_FORM_SCHEMA_VALIDATION(t, currentLanguage)),
    defaultValues,
    mode: FORM_MODE
  })

  const queryClient = useQueryClient()

  const { data: countries } = useQuery({ queryKey: ['countries'], queryFn: () => getCountries() })

  const { data: journeyPlaces } = useQuery({
    queryKey: ['journey-places'],
    queryFn: () => getJourneyPlaces()
  })

  const { data: users } = useQuery({
    queryKey: ['users', 'admins'],
    queryFn: getContactInParis
  })

  const { data: ols } = useQuery({
    queryKey: ['ols', { types: CENTER_OL_TYPES }],
    queryFn: () => getOls({ filter: { types: CENTER_OL_TYPES } }),
    enabled: form.watch('type') === OLTypeEnum.DIVERS
  })

  const mainContactPicture = form.watch('mainContactPicture')
  const mainContactName = toFullName(
    form.watch('mainContactFirstName'),
    form.watch('mainContactLastName')
  )
  const isActive = form.watch('isActive')
  const type = form.watch('type')
  const hostCountries = form.watch('hostCountries') ?? []
  const mainContactGender = form.watch('mainContactGender')

  // Fields are required only when gender is Male or Female (not Société/null),
  // and never for a Divers provider (a company with no account)
  const isPersonalInfoRequired = mainContactGender !== null && type !== OLTypeEnum.DIVERS

  const { mutate: deactivateOlMutation } = useMutation({
    mutationFn: deactivateOl,
    onSuccess: () => {
      toast.success('Le prestataire a été désactivé')
      queryClient.invalidateQueries({ queryKey: ['ol'] })
    },
    onError: showAxiosError
  })

  const { mutate: activateOlMutation } = useMutation({
    mutationFn: activateOl,
    onSuccess: () => {
      toast.success('Le prestataire a été activé')
      queryClient.invalidateQueries({ queryKey: ['ol'] })
    },
    onError: showAxiosError
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className="flex justify-between gap-4">
          <div className="flex-1">
            <div className="p-4 bg-primary-light rounded-xl border border-grey-light flex flex-col gap-3">
              <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
                Type
              </Typography>
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledSelectInput<OLTypeEnum>
                        required
                        labelClassName="bg-primary-light"
                        options={Object.values(OLTypeEnum).map((type) => ({
                          label: olTypeEnumMapping[type].label,
                          value: type
                        }))}
                        label={'Type de prestataire'}
                        selectedOption={getSelectedOption(
                          field.value,
                          olTypeEnumMapping[field.value]?.label
                        )}
                        onChange={(value) => {
                          form.setValue('type', value, { shouldValidate: true })
                          if (value !== OLTypeEnum.PARTNER) form.setValue('isOtoIndividual', false)
                          if (form.formState.isSubmitted) form.trigger()
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contactInParisId"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        optional
                        labelClassName="bg-primary-light"
                        label={'Contact à Paris'}
                        options={users?.data.map((user) => ({
                          label: toFullName(user.firstName, user.lastName),
                          value: user.id
                        }))}
                        selectedOption={getSelectedOption(
                          field.value,
                          toFullName(
                            users?.data.find((user) => user.id === field.value)?.firstName,
                            users?.data.find((user) => user.id === field.value)?.lastName
                          )
                        )}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {type === OLTypeEnum.PARTNER && (
                <FormField
                  control={form.control}
                  name="isOtoIndividual"
                  render={({ field }) => (
                    <FormItem className="bg-white w-fit rounded-sm border border-grey-light px-3 py-2">
                      <FormControl>
                        <Checkbox
                          {...field}
                          checked={field.value}
                          label={'OTO Indiv'}
                          onChange={(checked) => field.onChange(checked)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="h-4" />
            <div className="p-4 rounded-xl border border-grey-light flex flex-col gap-4">
              <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
                Informations Personnelles
              </Typography>
              <div>
                {!form.watch('mainContactPicture') && (
                  <Typography variant="tag">Télécharger la photo de profil</Typography>
                )}
                <div className="h-3" />
                <div className="flex gap-3 items-center">
                  <FormField
                    control={form.control}
                    name="mainContactPicture"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ProfileImageInput
                            title={'Photo de profil'}
                            file={mainContactPicture}
                            onFileChange={(value) =>
                              field.onChange(value, { shouldValidate: true })
                            }
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <div>
                    <div className="flex gap-2 items-center">
                      {mainContactName && (
                        <Typography variant="text">{mainContactName}</Typography>
                      )}
                      <Badge
                        text={isActive ? 'Actif' : 'Inactif'}
                        variant={isActive ? 'success' : 'danger'}
                        iconPosition="start"
                        icon={
                          <UserCheckIcon
                            width="24px"
                            height="24px"
                            color={colorsMapping[isActive ? 'success' : 'danger'].text}
                          />
                        }
                      />
                    </div>
                    <div className="h-2" />
                    {type && (
                      <Badge
                        text={olTypeEnumMapping[type].label}
                        variant={olTypeEnumMapping[type].variant}
                        color="white"
                        borderColor={tailwindColors.blue.light.DEFAULT}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div>
                <Typography variant="tag">Genre :</Typography>
                <div className="h-2" />
                <div className="flex gap-16">
                  {Object.values(genderEnumValues).map((gender) => (
                    <FormField
                      key={gender}
                      control={form.control}
                      name="mainContactGender"
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <FormControl>
                              <ControlledRadioInput
                                {...field}
                                label={genderOptionsMapping[gender].title}
                                checked={field.value === toGenderEnum(gender)}
                                onCheck={() => {
                                  field.onChange(toGenderEnum(gender))
                                  if (form.formState.isSubmitted) form.trigger()
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        )
                      }}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-between gap-3">
                <FormField
                  control={form.control}
                  name="mainContactLastName"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <ControlledInput {...field} required label="NOM" type="text" />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mainContactFirstName"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <ControlledInput
                          {...field}
                          required={isPersonalInfoRequired}
                          label="Prénom"
                          type="text"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-between gap-3">
                <FormField
                  control={form.control}
                  name="mainContactPosition"
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput
                          {...field}
                          required={isPersonalInfoRequired}
                          label={'Poste'}
                          type={'text'}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={'mainContactBirthDate'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledDateStringInput
                          {...field}
                          required={isPersonalInfoRequired}
                          label={'Date de naissance'}
                          iconIsRight
                          disabledFutureDates
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {form.watch('type') === OLTypeEnum.DIVERS && (
              <>
                <div className="h-4" />

                <div className="p-4 rounded-xl border border-grey-light flex flex-col gap-4">
                  <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
                    Prestataires divers
                  </Typography>

                  <FormField
                    control={form.control}
                    name={'relatedToOlIds'}
                    render={({ field }) => (
                      <FormItem className={'flex-1'}>
                        <FormControl>
                          <ControlledMultiSelectInput
                            {...field}
                            label={'Prestataires liés'}
                            variant={'primary'}
                            searchable
                            options={
                              ols?.data
                                .filter((ol) => ol.id !== olId)
                                .map((ol) => ({
                                  label: getOlName(ol),
                                  value: ol.id
                                })) ?? []
                            }
                            selectedOptions={field.value?.map((item) => ({
                              label: getOlName(ols?.data.find((ol) => ol.id === item)),
                              value: item
                            }))}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </>
            )}
          </div>

          <div className={'flex-1'}>
            <div className={'p-4 rounded-xl border border-grey-light flex flex-col gap-3'}>
              <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
                Adresse
              </Typography>
              <FormField
                control={form.control}
                name={'address1'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledAdressInput
                        {...field}
                        required
                        label={'Adresse'}
                        onDetailsChange={(placeDetails) => {
                          form.setValue('address1', placeDetails.address, { shouldValidate: true })
                          form.setValue('zipCode', placeDetails.postalCode, {
                            shouldValidate: true
                          })
                          form.setValue('city', placeDetails.city, { shouldValidate: true })
                          form.setValue('country', placeDetails.country, { shouldValidate: true })
                          form.trigger(['address1', 'zipCode', 'city', 'country'])
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address2"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} label="Complément d'adresse" type="text" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex justify-between gap-3">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <ControlledInput {...field} required label="Ville" type="text" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="zipCode"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <ControlledInput {...field} required label="Code postal" type="text" />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} required label="Pays" type="text" />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hostCountries"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledMultiSelectInput
                        {...field}
                        required
                        variant={'primary'}
                        label={'Pays de séjour'}
                        options={
                          countries?.data.map((country) => ({
                            label: country.name,
                            value: country.id
                          })) ?? []
                        }
                        selectedOptions={field.value?.map((id) => ({
                          label: countries?.data.find((country) => country.id === id)?.name ?? '',
                          value: id
                        }))}
                        onChange={(value) => {
                          field.onChange(value)
                          // Drop the stay places that belong to a country no longer selected
                          if (!journeyPlaces) return
                          form.setValue(
                            'hostPlaces',
                            (form.getValues('hostPlaces') ?? []).filter((id) => {
                              const countryId = journeyPlaces.data.find(
                                (place) => place.id === id
                              )?.countryId
                              return countryId !== undefined && value.includes(countryId)
                            })
                          )
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'hostPlaces'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledMultiSelectInput
                        {...field}
                        required
                        variant={'primary'}
                        searchable
                        label={'Lieux de séjour'}
                        options={
                          journeyPlaces?.data
                            .filter((place) => hostCountries.includes(place.countryId))
                            .map((place) => ({
                              label: place.name,
                              value: place.id
                            })) ?? []
                        }
                        selectedOptions={field.value?.map((id) => ({
                          label: journeyPlaces?.data.find((place) => place.id === id)?.name ?? '',
                          value: id
                        }))}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        label="Devise"
                        options={Object.values(CurrencyEnum).map((currency) => ({
                          label: currencyEnumMapping[currency].label,
                          value: currency
                        }))}
                        selectedOption={getSelectedEnumOption(field.value, currencyEnumMapping)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="h-4" />

            <div className="p-4 rounded-xl border border-grey-light flex flex-col gap-3">
              <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
                Contact Information
              </Typography>

              <FormField
                control={form.control}
                name="mainContactEmail"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} required label="Email" type="email" />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mainContactPhone"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledPhoneInput
                        {...field}
                        required
                        label={'Téléphone portable'}
                        placeholder={'ex : 06 12 34 56 78'}
                        defaultCountry={'FR'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mainContactPhone2"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledPhoneInput
                        {...field}
                        label={'Téléphone fixe'}
                        placeholder={'ex : 06 12 34 56 78'}
                        defaultCountry="FR"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="h-4" />
        <FormField
          control={form.control}
          name="files"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <FilesSelection
                  files={field.value ?? []}
                  onFilesChange={(files) =>
                    form.setValue('files', files, { shouldValidate: true })
                  }
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="h-4" />
        <OlBankAccountsOlForm />
        <div className="h-4" />
        <OlJourneyPlaceOlForm />
        <div className="h-4" />
        <OlContactsOlForm />
        <div className="h-4" />
        <OlCommentsOlForm />
        <div>
          <ValidationButtons
            preComponent={conditionalRender(
              !!olId,
              <StatusToggle
                isActive={isActive}
                onToggle={(checked) => {
                  form.setValue('isActive', checked, { shouldValidate: true })
                  if (checked) activateOlMutation(olId as number)
                  else deactivateOlMutation(olId as number)
                }}
              />
            )}
            {...validationButtonProps}
          />
        </div>
      </form>
    </Form>
  )
}

export default OlForm
